import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ErrorCode } from '@blk/wealth-web-endpoint';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { PortfolioAnalysisErrors } from '@ddt-web/common';
import { ERROR_SEVERITY, ErrorDCR, PAErrorDCR } from '@ddt-web/config';
import { ACPortfolioLibraryConfigKey, InjectConfig } from '@ddt-web/config-layer';
import { NotificationType } from '@ddt-web/notifications';
import * as get from 'lodash/get';
import { take } from 'rxjs/operators';

import { WwePortfolioAnalysisPartialError } from '../../models';
import { ErrorObject } from '../../models/error-object';
import { PopupDialogObject } from '../../models/popup-dialog-object';
import { AbstractErrorHandlerService } from '../abstract-error-handler.service';
import { ErrorHelperService } from '../error-helper.service';
import { ErrorPopupService } from '../error-popup.service';

@Injectable()
export class MonitoringPortfolioAnalysisErrorHandlerService extends AbstractErrorHandlerService {
	@InjectConfig(ACPortfolioLibraryConfigKey.DISABLE_MONITORING_HARD_STOP) public readonly disableHardStop: boolean = true;
	private flagErrorConfigs = ['details', 'risk', 'performance', 'rules'];
	private logOnlyErrorConfigs = ['modelPortfolios'];

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(
			contentService,
			errorPopupService,
			tealiumAnalyticsService,
			errorHelperService
		);
	}

	public handleError(error): void {
		const configName = get(error, 'configName', '');
		if (error instanceof ErrorObject && this.flagErrorConfigs.includes(configName)) {
			// Setup scope variables
			const errorObject = error.responseJson;
			const isPartialError = errorObject instanceof WwePortfolioAnalysisPartialError;

			// Set error severity
			this.errorSeverity = isPartialError ? ERROR_SEVERITY.ERROR : ERROR_SEVERITY.CRITICAL;
			this.httpRequestId = error?.responseJson?.headers?.get('x-request-id');

			if (isPartialError) {
				const errorCodes = errorObject.errorCodes;
				// Handle partial error
				const partialErrorCode = errorCodes.get(PortfolioAnalysisErrors.PARTIAL_ERROR);
				this.handlePartialError(partialErrorCode);
			} else if (errorObject?.errorCode === PortfolioAnalysisErrors.GENERAL_ERROR && !this.disableHardStop) {
				console.error('There was an error while retrieving data for config: ' + configName, errorObject);
				// Build default error object
				const dialogErrorMessage = this.handleDialogErrorMessage();
				dialogErrorMessage.trackMsgPrefix = this.errorSeverity;
				this.errorPopupService.openResponseMessageDialog(dialogErrorMessage, true);
			}

			this.handleErrorTracking('unabletoprocessrequest', this.errorSeverity);
		} else if (error instanceof ErrorObject && this.logOnlyErrorConfigs.includes(configName)) {
			const errorObject = error.responseJson;
			console.error('There was an error while retrieving data for config: ' + configName, errorObject);
			this.handleErrorTracking('unabletoprocessrequest', ERROR_SEVERITY.ERROR);
		} else {
			this.nextHandler.handleError(error);
		}
	}

	private handlePartialError(errorCode: ErrorCode) {
		const portfolioNumbers = errorCode.description.split('|');
		const defaultErrorMessage = '$failedPortfolios$ of $totalPortfolios$ portfolio(s) you selected could not be imported due to few issues. '
			+ 'Please see the table below to identify the portfolio to remove or edit.';
		const message = this.contentService.get('MONITORING_PARTIAL_ERROR_MESSAGE', defaultErrorMessage)
			.replace('$failedPortfolios$', portfolioNumbers[0]).replace('$totalPortfolios$', portfolioNumbers[1]);

		this.errorHelperService.getVisibleNotificationsByChannel$(['monitoring_errors']).pipe(take(1)).subscribe(notifications => {
			if (notifications.length) {
				notifications.forEach(notification => this.errorHelperService.dismissNotification(notification.id));
			}
			this.errorHelperService.addNotification('monitoring_errors', message, 'exclamation-circle', null, NotificationType.ERROR);

		});
	}

	private handleDialogErrorMessage(): PopupDialogObject {
		const description = this.contentService.get('PORTFOLIO_ANALYSIS_COMMON_ERROR', ErrorDCR.PORTFOLIO_ANALYSIS_COMMON_ERROR);
		const label = this.contentService.get('PA_RETRY_MESSAGE', PAErrorDCR.PA_RETRY_LABEL);

		return this.errorHelperService.buildPopupDialogObject(description, label, true, this.buildErrorMessage());
	}
}
